import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  esErrorServicioNoActivo,
  limpiarServicioActivoLocal,
} from "../../utils/servicioActivo";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import {
  AlertTriangle,
  Calculator,
  CheckCircle,
  ExternalLink,
  History,
  Info,
  Eye,
  Printer,
  RotateCcw,
  Save,
  Tag,
  Thermometer,
  WifiOff,
} from "lucide-react";
import { descargarReporteIndividual } from "../../utils/reports";
import {
  asignarSticker,
  imprimirSticker,
  stickerDeCronograma,
  urlCertificadoPublico,
} from "../../utils/calibStickers";
import AsignarStickerModal from "../calibracion/AsignarStickerModal";
import VistaPreviaReporteModal from "../calibracion/VistaPreviaReporteModal";
import DetalleCalculosModal from "../calibracion/DetalleCalculosModal";
import {
  DECIMALES_PRESENTACION,
  decimalesPresentacion,
  emparejarMediciones,
  estadoPatronEn,
  formatear,
  num,
  patronesDePlantilla,
  plantillasCompatibles,
  procesarCalibracion,
  toleranciaPermitida,
  toleranciasDePunto,
  variableDePatron,
} from "../../utils/calibCalculo";

const API = process.env.REACT_APP_API_URL;

const idDeRef = (v) => (v && v._id ? String(v._id) : v ? String(v) : "");

const hoyLocal = () => new Date().toLocaleDateString("en-CA"); // "YYYY-MM-DD"

// ─── Borrador local (sobrevive a recargas y pérdida de conexión) ──────────
const draftKey = (servicioId) => `calibDraft:${servicioId}`;

const leerBorradorLocal = (servicioId) => {
  try {
    return JSON.parse(localStorage.getItem(draftKey(servicioId)) || "null");
  } catch {
    return null;
  }
};

const borrarBorradorLocal = (servicioId) => {
  try {
    localStorage.removeItem(draftKey(servicioId));
  } catch {
    // almacenamiento no disponible
  }
};

// Mediciones vacías (variable × punto, clave: índice del punto) y parámetros fijos de una plantilla
const estructuraDePlantilla = (tpl) => {
  const mediciones = [];
  for (const variable of [...(tpl.variables || [])].sort(
    (a, b) => (a.orden ?? 0) - (b.orden ?? 0),
  )) {
    const puntos = variable.puntosCalib?.length
      ? variable.puntosCalib
      : [{ valor: "", etiqueta: "Único" }];
    // Variable vinculada: el patrón lo fija la plantilla
    const patronId =
      variable.patron && variable.patronVariable
        ? idDeRef(variable.patron)
        : "";

    puntos.forEach((punto, puntoIndex) => {
      const tol = toleranciasDePunto(variable, punto);
      mediciones.push({
        variableId: variable._id,
        nombreVariable: variable.nombre,
        unidad: variable.unidad,
        puntoReferencia: punto.valor,
        puntoIndex,
        puntosLabel: punto.etiqueta || String(punto.valor),
        repeticiones: Array.from(
          { length: tpl.repeticionesPorPunto || 3 },
          () => ({
            valorMedido: "",
            valorPatron: "",
            patronId,
            timestamp: null,
          }),
        ),
        comentario: "",
        medida: true,
        _toleranciaAbs: tol.abs,
        _toleranciaPct: tol.pct,
        _toleranciaPropia: tol.propia,
      });
    });
  }
  const parametros = {};
  for (const p of tpl.parametrosFijos || [])
    parametros[p.clave] = p.valor || "";
  return { mediciones, parametros };
};

// Patrón común de una variable: el de todos sus puntos si coinciden, si no "".
const patronComun = (puntos) => {
  const ids = new Set(
    puntos.flatMap((m) => (m.repeticiones || []).map((r) => r.patronId || "")),
  );
  return ids.size === 1 ? [...ids][0] : "";
};

// ─── Condiciones ambientales (temperatura / humedad, inicio y final) ──────
const ambienteVacio = () => ({
  incluir: true,
  tempInicio: "",
  tempFinal: "",
  humInicio: "",
  humFinal: "",
  patron: "",
});

const promedioAmb = (a, b) => {
  const x = num(a);
  const y = num(b);
  return x === null || y === null ? null : parseFloat(((x + y) / 2).toFixed(2));
};

const ambienteDeGestion = (c, prev) => {
  const v = (n) => (n === null || n === undefined ? "" : String(n));
  return {
    incluir: c.incluir !== false,
    tempInicio: v(c.tempInicio),
    tempFinal: v(c.tempFinal),
    humInicio: v(c.humInicio),
    humFinal: v(c.humFinal),
    patron: idDeRef(c.patron) || prev.patron,
  };
};

// Una variable se mide salvo que el calibrador la desactive (no aplica al equipo)
const esMedida = (m) => m?.medida !== false;

const idDe = (v) => (v && v._id ? String(v._id) : v ? String(v) : "");

// ─── Memoria de calibración por categoría + marca + modelo ────────────────
// Configuración recordada de una variable: por id y, si la plantilla cambió, por nombre.
const buscarVariablePerfil = (perfilVariables = [], variableId, nombre) => {
  const porId = perfilVariables.find(
    (v) => idDe(v.variableId) === String(variableId),
  );
  if (porId) return porId;
  const n = String(nombre || "")
    .trim()
    .toLowerCase();
  return (
    perfilVariables.find(
      (v) =>
        String(v.nombre || "")
          .trim()
          .toLowerCase() === n,
    ) || null
  );
};

// Aplica el perfil a mediciones recién inicializadas. Solo usa patrones vigentes.
const aplicarPerfilAMediciones = (perfil, mediciones, patronesDisponibles) => {
  const vigentes = new Set(
    patronesDisponibles
      .filter((p) => p.estado === "vigente")
      .map((p) => String(p._id)),
  );
  const patronesSeleccionados = (perfil.patronesUsados || [])
    .map(idDe)
    .filter((id) => vigentes.has(id));
  const elegibles = new Set(patronesSeleccionados);
  const patronPorVariable = {};

  const meds = mediciones.map((m) => {
    const v = buscarVariablePerfil(
      perfil.variables,
      m.variableId,
      m.nombreVariable,
    );
    if (!v) return m;
    const patronId = elegibles.has(idDe(v.patronId)) ? idDe(v.patronId) : "";
    if (patronId) patronPorVariable[m.variableId] = patronId;
    return {
      ...m,
      medida: v.medida !== false,
      repeticiones: patronId
        ? m.repeticiones.map((r) => ({ ...r, patronId }))
        : m.repeticiones,
    };
  });
  return { mediciones: meds, patronesSeleccionados, patronPorVariable };
};

// ─── Rango de aceptación por punto (misma regla que el motor de cálculo) ──
const fmtNum = (n) => formatear(n, DECIMALES_PRESENTACION);
const SEPARADOR_REGLA = { mayor: " o ", menor: " y ", suma: " + " };

const rangoAceptacion = (
  puntoReferencia,
  toleranciaAbs,
  toleranciaPct,
  regla,
) => {
  const ref = num(puntoReferencia);
  if (ref === null) return null;
  const abs = toleranciaAbs;
  const pct = toleranciaPct;
  const { delta } = toleranciaPermitida(ref, abs, pct, regla);
  if (delta === null) return null;
  const partes = [];
  if (abs !== null) partes.push(`±${fmtNum(abs)}`);
  if (pct !== null) partes.push(`±${fmtNum(pct)}%`);
  return {
    min: fmtNum(ref - delta),
    max: fmtNum(ref + delta),
    etiqueta: partes.join(SEPARADOR_REGLA[regla] || " o "),
  };
};

// ─── Badge de decisión ─────────────────────────────────────────────────────
const DECISION_UI = {
  conforme: [
    "Conforme",
    "text-green-700 bg-green-50 border-green-200",
    CheckCircle,
  ],
  no_conforme: [
    "No conforme",
    "text-red-700 bg-red-50 border-red-200",
    AlertTriangle,
  ],
  indeterminado: [
    "Indeterminado",
    "text-amber-700 bg-amber-50 border-amber-200",
    AlertTriangle,
  ],
  sin_criterio: [
    "Sin criterio",
    "text-gray-600 bg-gray-50 border-gray-200",
    Info,
  ],
};

const DecisionBadge = ({ decision }) => {
  const d = DECISION_UI[decision];
  if (!d) return null;
  const [label, clases, Icono] = d;
  return (
    <span
      className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border whitespace-nowrap ${clases}`}
    >
      <Icono size={11} /> {label}
    </span>
  );
};

// Solo los datos capturados viajan al servidor: él recalcula todos los resultados
const medicionCapturada = (m) => ({
  variableId: m.variableId,
  nombreVariable: m.nombreVariable,
  unidad: m.unidad,
  puntoReferencia: m.puntoReferencia,
  puntoIndex: m.puntoIndex,
  comentario: m.comentario || "",
  medida: m.medida !== false,
  repeticiones: (m.repeticiones || []).map((r) => ({
    valorMedido: r.valorMedido ?? "",
    valorPatron: r.valorPatron ?? "",
    patronId: r.patronId || null,
    timestamp: r.timestamp || null,
  })),
});

// Lecturas guardadas (número o null) → texto de los inputs
const aTexto = (v) => (v === null || v === undefined ? "" : String(v));
const repeticionesEditables = (reps = []) =>
  reps.map((r) => ({
    ...r,
    valorMedido: aTexto(r.valorMedido),
    valorPatron: aTexto(r.valorPatron),
    patronId: idDe(r.patronId),
  }));

// Patrón sin variables configuradas o con alguna de la misma magnitud
const patronCompatible = (patron, variable) => {
  const n = (s) =>
    String(s || "")
      .trim()
      .toLowerCase();
  const magnitudes = (patron?.variablesMedicion || [])
    .map((v) => n(v.magnitud))
    .filter(Boolean);
  return (
    !variable?.magnitud ||
    !magnitudes.length ||
    magnitudes.includes(n(variable.magnitud))
  );
};

// ─── Componente principal ──────────────────────────────────────────────────
const FormCalibracion = ({
  servicio,
  gestionData,
  canEdit = false,
  tiempoSegundos = 0,
  soloRevision = false, // segundo firmante: sin acciones sobre el sticker
  onDraftSaved,
  onFinalized,
}) => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // Estado principal
  const [template, setTemplate] = useState(null);
  // Plantillas activas de la categoría cuando aún no se eligió una (una por patrón)
  const [plantillasCandidatas, setPlantillasCandidatas] = useState([]);
  const [patronesDisponibles, setPatronesDisponibles] = useState([]);
  const [patronesSeleccionados, setPatronesSeleccionados] = useState([]);
  const [mediciones, setMediciones] = useState([]);
  const [parametrosFijosForm, setParametrosFijosForm] = useState({});
  const [observaciones, setObservaciones] = useState("");
  const [fechaCalibracion, setFechaCalibracion] = useState(hoyLocal); // "YYYY-MM-DD"
  // La vigencia de los patrones se evalúa en la fecha de calibración
  const fechaVigencia = useMemo(
    () =>
      fechaCalibracion ? new Date(`${fechaCalibracion}T12:00:00-05:00`) : new Date(),
    [fechaCalibracion],
  );
  const [erroresValidacion, setErroresValidacion] = useState([]);
  const [gestionExistente, setGestionExistente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [modoSolo, setModoSolo] = useState(false); // true si hay gestión Finalizada
  const [patronPorVariable, setPatronPorVariable] = useState({});
  const [gestionCargada, setGestionCargada] = useState(false);
  const [hidratado, setHidratado] = useState(false);
  const [ultimoAutoguardado, setUltimoAutoguardado] = useState(null);
  const [enLinea, setEnLinea] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  const borradorRestaurado = useRef(false);
  const [sticker, setSticker] = useState(null);
  const [modalSticker, setModalSticker] = useState(null); // "finalizar" | "asignar"
  // Vista previa del PDF antes de finalizar: { url, paraFinalizar }
  const [vistaPrevia, setVistaPrevia] = useState(null);
  const [generandoVista, setGenerandoVista] = useState(false);
  const [detalleVariableId, setDetalleVariableId] = useState(null); // modal "Mostrar cálculos"
  const [perfil, setPerfil] = useState(null); // memoria por categoría+marca+modelo
  const [perfilAplicado, setPerfilAplicado] = useState(false);
  const [ambiente, setAmbiente] = useState(ambienteVacio);

  const equipo = servicio?.equipo;
  const perfilQuery = {
    categoria: idDe(equipo?.categoria),
    marca: idDe(equipo?.marca),
    modelo: idDe(equipo?.modelo),
  };

  // Sticker ya asignado a una calibración finalizada, o a una devuelta por el
  // segundo firmante (en borrador, conserva su código al finalizar de nuevo)
  useEffect(() => {
    if ((!modoSolo && servicio?.estado !== "Borrador") || !servicio?._id) return;
    stickerDeCronograma(servicio._id)
      .then(setSticker)
      .catch(() => setSticker(null));
  }, [modoSolo, servicio]);

  const aplicarPlantilla = (tpl) => {
    const { mediciones: estructura, parametros } = estructuraDePlantilla(tpl);
    setTemplate(tpl);
    setMediciones(estructura);
    setParametrosFijosForm(parametros);
  };

  // Elección por patrones: se carga sola cuando solo una plantilla (con
  // patrones) es compatible y ninguna otra podría serlo al marcar más patrones.
  useEffect(() => {
    if (
      template ||
      !plantillasCandidatas.length ||
      !patronesSeleccionados.length
    )
      return;
    const sel = patronesSeleccionados.map(String);
    const conPatrones = plantillasCompatibles(plantillasCandidatas, sel).filter(
      (t) => patronesDePlantilla(t).length > 0,
    );
    const ampliables = plantillasCandidatas.filter((t) => {
      const ids = patronesDePlantilla(t);
      return ids.length > sel.length && sel.every((id) => ids.includes(id));
    });
    if (conPatrones.length === 1 && !ampliables.length)
      aplicarPlantilla(conPatrones[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patronesSeleccionados, plantillasCandidatas, template]);

  const cambiarPlantilla = () => {
    if (
      !window.confirm(
        "¿Cambiar de plantilla? Se descartan las lecturas no guardadas.",
      )
    )
      return;
    borrarBorradorLocal(servicio._id);
    setTemplate(null);
    setMediciones([]);
    setPatronesSeleccionados([]);
    setPatronPorVariable({});
  };

  // ── Cargar plantilla, patrones y memoria del modelo ───────────────────
  // Una calibración ya iniciada conserva SU plantilla (la instantánea si está
  // finalizada); solo una calibración nueva toma la plantilla activa.
  useEffect(() => {
    const categoriaId = servicio?.equipo?.categoria?._id;
    if (!categoriaId) {
      toast.error("El equipo no tiene categoría asignada");
      setLoading(false);
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };

    const fetchData = async () => {
      try {
        const marcaId = idDe(servicio?.equipo?.marca);
        const modeloId = idDe(servicio?.equipo?.modelo);
        // Patrón ambiental predeterminado del usuario (último usado)
        axios
          .get(`${API}/gestion/calibracion/patron-ambiental`, { headers })
          .then((r) => {
            const def = idDeRef(r.data?.patron);
            if (def)
              setAmbiente((prev) =>
                prev.patron ? prev : { ...prev, patron: def },
              );
          })
          .catch(() => {});

        const previa =
          gestionData ||
          (await axios
            .get(`${API}/gestion/cronograma/${servicio._id}`, { headers })
            .then((r) => r.data?.[0] || null)
            .catch(() => null));
        // Plantilla: instantánea (finalizada) > la de la gestión > elegida por patrones
        const tplId = idDe(previa?.calibrationTemplate);
        let tpl = previa?.snapshot?.plantilla || null;
        if (!tpl && tplId) {
          tpl = await axios
            .get(`${API}/calib-templates/${tplId}`, { headers })
            .then((r) => r.data)
            .catch(() => null);
        }
        let candidatas = [];
        if (!tpl) {
          candidatas = await axios
            .get(`${API}/calib-templates/categoria/${categoriaId}/activos`, {
              headers,
            })
            .then((r) => r.data || [])
            .catch(() => []);
          const draft = leerBorradorLocal(servicio._id);
          tpl =
            candidatas.find(
              (t) =>
                draft?.templateId && String(t._id) === String(draft.templateId),
            ) || (candidatas.length === 1 ? candidatas[0] : null);
        }

        const [patronesRes, perfilRes] = await Promise.all([
          axios.get(`${API}/calib-patrones`, { headers }),
          marcaId && modeloId
            ? axios
                .get(`${API}/calib-perfiles`, {
                  headers,
                  params: {
                    categoria: categoriaId,
                    marca: marcaId,
                    modelo: modeloId,
                  },
                })
                .catch(() => ({ data: null }))
            : Promise.resolve({ data: null }),
        ]);

        setPatronesDisponibles(patronesRes.data);
        setPerfil(perfilRes.data || null);

        if (tpl) {
          aplicarPlantilla(tpl);
        } else if (candidatas.length) {
          // Varias plantillas: se elige según los patrones (precarga los recordados del modelo)
          setPlantillasCandidatas(candidatas);
          const vigentes = new Set(
            patronesRes.data
              .filter((p) => estadoPatronEn(p) === "vigente")
              .map((p) => String(p._id)),
          );
          const recordados = (perfilRes.data?.patronesUsados || [])
            .map(idDe)
            .filter((id) => vigentes.has(id));
          if (recordados.length) setPatronesSeleccionados(recordados);
        } else {
          toast.warning(
            "No hay plantilla activa para esta categoría. Configura una en Ajustes > Categorías.",
          );
        }
      } catch {
        toast.error("Error al cargar la plantilla de calibración");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servicio, token]);

  // Fusiona mediciones guardadas sobre la estructura de la plantilla por
  // variable + índice de punto (dos puntos con el mismo nominal no se cruzan)
  const avisoHuerfanas = useRef(false);
  const fusionarGuardadas = (estructura, guardadas = []) => {
    if (!estructura.length) {
      return guardadas.map((m) => ({
        ...m,
        repeticiones: repeticionesEditables(m.repeticiones),
      }));
    }
    const { pares, huerfanas } = emparejarMediciones(estructura, guardadas);
    if (huerfanas.length && !avisoHuerfanas.current) {
      avisoHuerfanas.current = true;
      toast.warning(
        `${huerfanas.length} medición(es) guardada(s) ya no corresponden a los puntos de la plantilla y no se muestran`,
      );
    }
    return pares.map(({ estructura: m, guardada }) =>
      guardada
        ? {
            ...m,
            ...guardada,
            puntoIndex: m.puntoIndex,
            puntosLabel: m.puntosLabel,
            repeticiones: repeticionesEditables(guardada.repeticiones),
          }
        : m,
    );
  };

  // ── Cargar gestión existente (borrador o finalizada) ────────────────────
  useEffect(() => {
    const cargarGestion = async () => {
      const fuente = gestionData || null;
      if (fuente) {
        aplicarGestionExistente(fuente);
        setGestionCargada(true);
        return;
      }
      try {
        const res = await axios.get(
          `${API}/gestion/cronograma/${servicio._id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.data?.length) aplicarGestionExistente(res.data[0]);
      } catch {
        // sin gestión previa
      } finally {
        setGestionCargada(true);
      }
    };
    cargarGestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servicio, gestionData]);

  // Reaplicar valores guardados cuando ya existe plantilla cargada
  useEffect(() => {
    if (!template || !gestionExistente?.mediciones?.length) return;
    setMediciones((prev) =>
      fusionarGuardadas(prev, gestionExistente.mediciones),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, gestionExistente]);

  const aplicarGestionExistente = (g) => {
    setGestionExistente(g);
    setObservaciones(g.observaciones || "");
    setFechaCalibracion(g.fechaCalibracion ? String(g.fechaCalibracion).slice(0, 10) : "");
    if (g.patronesUsados?.length) {
      setPatronesSeleccionados(g.patronesUsados.map((p) => String(p._id || p)));
    }
    if (g.parametrosFijosRegistrados) {
      setParametrosFijosForm(g.parametrosFijosRegistrados);
    }
    if (g.condicionesAmbientales) {
      setAmbiente((prev) => ambienteDeGestion(g.condicionesAmbientales, prev));
    }
    if (g.mediciones?.length) {
      setMediciones((prev) => fusionarGuardadas(prev, g.mediciones));
    }
    if (g.estado === "Finalizado") setModoSolo(true);
  };

  // ── Restaurar borrador local (una sola vez, tras cargar plantilla y gestión)
  useEffect(() => {
    if (borradorRestaurado.current || loading || !gestionCargada || !template)
      return;
    borradorRestaurado.current = true;

    const draft = leerBorradorLocal(servicio._id);
    const servidorMasNuevo =
      draft &&
      gestionExistente?.updatedAt &&
      new Date(gestionExistente.updatedAt) > new Date(draft.savedAt);
    const otraPlantilla =
      draft?.templateId &&
      template &&
      String(draft.templateId) !== String(template._id);

    if (modoSolo || servidorMasNuevo || otraPlantilla) {
      borrarBorradorLocal(servicio._id);
    } else if (draft && template) {
      setMediciones((prev) => {
        const { pares } = emparejarMediciones(prev, draft.mediciones || []);
        return pares.map(({ estructura: m, guardada }) =>
          guardada
            ? {
                ...m,
                repeticiones: repeticionesEditables(
                  guardada.repeticiones || m.repeticiones,
                ),
                comentario: guardada.comentario ?? m.comentario,
                medida: guardada.medida ?? m.medida,
              }
            : m,
        );
      });
      if (typeof draft.observaciones === "string") {
        setObservaciones(draft.observaciones);
      }
      if (typeof draft.fechaCalibracion === "string") {
        setFechaCalibracion(draft.fechaCalibracion);
      }
      if (draft.parametrosFijosForm) {
        setParametrosFijosForm(draft.parametrosFijosForm);
      }
      if (Array.isArray(draft.patronesSeleccionados)) {
        setPatronesSeleccionados(draft.patronesSeleccionados);
      }
      if (draft.patronPorVariable)
        setPatronPorVariable(draft.patronPorVariable);
      if (draft.ambiente) {
        setAmbiente((prev) => ({
          ...prev,
          ...draft.ambiente,
          patron: draft.ambiente.patron || prev.patron,
        }));
      }
      setUltimoAutoguardado(draft.savedAt);
      toast.info("Se restauraron los datos guardados en este dispositivo");
    } else if (!draft && !gestionExistente && template && perfil) {
      // Calibración nueva: precargar la configuración recordada del modelo
      const aplicado = aplicarPerfilAMediciones(
        perfil,
        mediciones,
        patronesDisponibles,
      );
      setMediciones(aplicado.mediciones);
      setPatronesSeleccionados(aplicado.patronesSeleccionados);
      setPatronPorVariable(aplicado.patronPorVariable);
      setPerfilAplicado(true);
    }
    setHidratado(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, gestionCargada, template, gestionExistente, modoSolo, servicio]);

  // Los patrones vinculados en la plantilla siempre forman parte de la calibración
  useEffect(() => {
    if (!template || modoSolo || !hidratado) return;
    const ids = patronesDePlantilla(template);
    if (ids.length) {
      setPatronesSeleccionados((prev) => [
        ...new Set([...prev.map(String), ...ids]),
      ]);
    }
  }, [template, modoSolo, hidratado]);

  // ── Resultados: SIEMPRE derivados de los datos capturados ──────────────
  // Mismo motor que el servidor (utils/calibCalculo): cualquier cambio de
  // lectura, patrón o plantilla se refleja de inmediato. Una calibración
  // finalizada muestra los resultados sellados por el servidor.
  const calculo = useMemo(() => {
    if (!template) {
      return {
        mediciones: [],
        resultadoGlobal: null,
        errores: [],
        advertencias: [],
      };
    }
    if (modoSolo) {
      return {
        mediciones,
        resultadoGlobal: gestionExistente?.resultadoGlobal || null,
        tipoDocumento: gestionExistente?.calculo?.tipoDocumento || null,
        errores: [],
        advertencias: gestionExistente?.calculo?.advertencias || [],
      };
    }
    return procesarCalibracion({
      plantilla: template,
      mediciones,
      patrones: patronesDisponibles,
      condicionesAmbientales: ambiente,
      fecha: fechaVigencia,
      finalizar: false,
    });
  }, [
    template,
    modoSolo,
    mediciones,
    gestionExistente,
    patronesDisponibles,
    ambiente,
    fechaVigencia,
  ]);
  const resultadoGlobal = calculo.resultadoGlobal;

  // ── Autoguardado local de cada dato ingresado ─────────────────────────
  const primerAutoguardado = useRef(true);
  useEffect(() => {
    if (!hidratado || modoSolo || !template) return;
    // El primer render tras hidratar solo refleja lo ya guardado
    if (primerAutoguardado.current) {
      primerAutoguardado.current = false;
      return;
    }
    const savedAt = new Date().toISOString();
    try {
      localStorage.setItem(
        draftKey(servicio._id),
        JSON.stringify({
          savedAt,
          templateId: template._id,
          mediciones,
          observaciones,
          fechaCalibracion,
          parametrosFijosForm,
          patronesSeleccionados,
          patronPorVariable,
          ambiente,
        }),
      );
      setUltimoAutoguardado(savedAt);
    } catch {
      // almacenamiento lleno o no disponible
    }
  }, [
    hidratado,
    modoSolo,
    template,
    servicio,
    mediciones,
    observaciones,
    fechaCalibracion,
    parametrosFijosForm,
    patronesSeleccionados,
    patronPorVariable,
    ambiente,
  ]);

  // ── Estado de conexión ────────────────────────────────────────────────
  useEffect(() => {
    const onOnline = () => setEnLinea(true);
    const onOffline = () => setEnLinea(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  // ── Actualizar valor de repetición ────────────────────────────────────
  const updateRepeticion = (medIdx, repIdx, field, value) => {
    setMediciones((prev) => {
      const meds = [...prev];
      const med = { ...meds[medIdx] };
      const reps = [...med.repeticiones];
      reps[repIdx] = { ...reps[repIdx], [field]: value };
      if (field === "valorMedido" || field === "valorPatron")
        reps[repIdx].timestamp = new Date().toISOString();
      med.repeticiones = reps;
      meds[medIdx] = med;
      return meds;
    });
  };

  const updateMedicionField = (idx, field, value) => {
    setMediciones((prev) => {
      const meds = [...prev];
      meds[idx] = { ...meds[idx], [field]: value };
      return meds;
    });
  };

  // Patrón de la variable: se aplica a todos sus puntos (luego editable por punto)
  const aplicarPatronVariable = (variableId, patronId) => {
    setPatronPorVariable((prev) => ({ ...prev, [variableId]: patronId }));
    setMediciones((prev) =>
      prev.map((m) =>
        String(m.variableId) === String(variableId)
          ? {
              ...m,
              repeticiones: m.repeticiones.map((r) => ({ ...r, patronId })),
            }
          : m,
      ),
    );
  };

  // Activar/desactivar la medición de una variable (todos sus puntos)
  const setVariableMedida = (variableId, medida) => {
    setMediciones((prev) =>
      prev.map((m) =>
        String(m.variableId) === String(variableId) ? { ...m, medida } : m,
      ),
    );
  };

  // Volver a la plantilla por defecto y olvidar la memoria del modelo
  const olvidarPerfil = async () => {
    setMediciones((prev) =>
      prev.map((m) => ({
        ...m,
        medida: true,
        repeticiones: m.repeticiones.map((r) => ({ ...r, patronId: "" })),
      })),
    );
    setPatronesSeleccionados([]);
    setPatronPorVariable({});
    setPerfilAplicado(false);
    setPerfil(null);
    try {
      await axios.delete(`${API}/calib-perfiles`, {
        headers: { Authorization: `Bearer ${token}` },
        params: perfilQuery,
      });
      toast.info("Configuración recordada eliminada para este modelo");
    } catch {
      toast.error("No se pudo eliminar la configuración recordada");
    }
  };

  const togglePatron = (id) => {
    setPatronesSeleccionados((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  };

  // Mismas validaciones de cierre que aplica el servidor
  const validarAntesDeFinalizar = () => {
    const r = procesarCalibracion({
      plantilla: template,
      mediciones,
      patrones: patronesDisponibles,
      condicionesAmbientales: ambiente,
      fecha: fechaVigencia,
      finalizar: true,
    });
    setErroresValidacion(r.errores);
    if (r.errores.length) {
      toast.error(
        `No se puede finalizar: ${r.errores[0]}${
          r.errores.length > 1 ? ` (y ${r.errores.length - 1} más)` : ""
        }`,
      );
      return false;
    }
    return true;
  };

  const mostrarErroresServidor = (err) => {
    const errores = err.response?.data?.errores;
    if (Array.isArray(errores)) setErroresValidacion(errores);
  };

  const buildPayload = (estado = "Gestionado") => ({
    cronograma_id: servicio._id,
    tipo: "Calibración",
    estado,
    observaciones,
    fechaCalibracion,
    calibrationTemplate: template?._id,
    patronesUsados: patronesSeleccionados,
    mediciones: mediciones.map(medicionCapturada),
    parametrosFijosRegistrados: parametrosFijosForm,
    condicionesAmbientales: {
      ...ambiente,
      patron: ambiente.patron || null,
    },
    tiempoInvertidoMinutos: Math.max(1, Math.round(tiempoSegundos / 60)),
  });

  const handleGuardarBorrador = async () => {
    try {
      setGuardando(true);
      const payload = buildPayload("Gestionado");
      if (gestionExistente) {
        const res = await axios.put(
          `${API}/gestion/${gestionExistente._id}`,
          payload,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        onDraftSaved?.(res.data?.gestion);
      } else {
        const res = await axios.post(`${API}/gestion`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setGestionExistente(res.data?.gestion || null);
        onDraftSaved?.(res.data?.gestion);
      }
      setErroresValidacion([]);
      localStorage.removeItem("servicioActivo");
      const userStored = JSON.parse(localStorage.getItem("user") || "{}");
      userStored.servicioActivo = null;
      userStored.servicioInicio = null;
      localStorage.setItem("user", JSON.stringify(userStored));
      toast.success("Borrador guardado correctamente");
    } catch (err) {
      if (esErrorServicioNoActivo(err)) limpiarServicioActivoLocal();
      mostrarErroresServidor(err);
      toast.error(err.response?.data?.message || "Error al guardar");
    } finally {
      setGuardando(false);
    }
  };

  // PDF calculado por el servidor con los datos actuales, sin guardar nada
  const abrirVistaPrevia = async (paraFinalizar = false) => {
    try {
      setGenerandoVista(true);
      const res = await axios.post(
        `${API}/gestion/calibracion/vista-previa`,
        buildPayload(),
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        },
      );
      const url = URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" }),
      );
      setVistaPrevia((prev) => {
        if (prev?.url) URL.revokeObjectURL(prev.url);
        return { url, paraFinalizar };
      });
    } catch (err) {
      // Con responseType "blob" el error JSON llega como Blob
      let mensaje = "No se pudo generar la vista previa";
      try {
        const data = JSON.parse(await err.response?.data?.text());
        mensaje = data.message || mensaje;
      } catch {
        /* respuesta sin JSON */
      }
      toast.error(mensaje);
    } finally {
      setGenerandoVista(false);
    }
  };

  const cerrarVistaPrevia = () => {
    setVistaPrevia((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
  };

  // Finalizar: vista previa del reporte → confirmación → modal de sticker
  const handleFinalizar = () => {
    if (!validarAntesDeFinalizar()) return;
    abrirVistaPrevia(true);
  };

  const confirmarDesdeVistaPrevia = () => {
    cerrarVistaPrevia();
    if (sticker) {
      finalizarConSticker();
      return;
    }
    setModalSticker("finalizar");
  };

  const asignarStickerServicio = async (stickerId) => {
    try {
      const res = await asignarSticker(servicio._id, stickerId);
      setSticker(res.sticker);
      toast.success(`Sticker ${res.sticker.numero} asignado`);
    } catch (err) {
      if (err.response?.data?.sticker) setSticker(err.response.data.sticker);
      toast.error(
        err.response?.data?.message || "No se pudo asignar el sticker",
      );
    }
  };

  const handleAsignarDespues = async (stickerId) => {
    setGuardando(true);
    await asignarStickerServicio(stickerId);
    setGuardando(false);
    setModalSticker(null);
  };

  const finalizarConSticker = async (stickerId) => {
    try {
      setGuardando(true);
      const payload = buildPayload("Finalizado");
      if (gestionExistente) {
        const res = await axios.put(
          `${API}/gestion/${gestionExistente._id}`,
          payload,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        onFinalized?.(res.data?.gestion);
      } else {
        const res = await axios.post(`${API}/gestion`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setGestionExistente(res.data.gestion);
        onFinalized?.(res.data?.gestion);
      }
      localStorage.removeItem("servicioActivo");
      borrarBorradorLocal(servicio._id);
      toast.success("Calibración registrada.");
      if (!sticker) await asignarStickerServicio(stickerId);
      setModalSticker(null);
    } catch (err) {
      if (esErrorServicioNoActivo(err)) limpiarServicioActivoLocal();
      mostrarErroresServidor(err);
      toast.error(err.response?.data?.message || "Error al finalizar");
    } finally {
      setGuardando(false);
    }
  };

  // ── Renders ────────────────────────────────────────────────────────────
  if (loading)
    return (
      <p className="text-gray-400 italic text-sm">
        Cargando plantilla de calibración...
      </p>
    );

  if (!template && plantillasCandidatas.length) {
    const sel = patronesSeleccionados.map(String);
    const compatibles = sel.length
      ? plantillasCompatibles(plantillasCandidatas, sel)
      : [];
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-green-700">
          Ejecución de Calibración
        </h3>
        <p className="text-sm text-gray-600">
          La categoría <strong>{servicio?.equipo?.categoria?.name}</strong>{" "}
          tiene varias plantillas. Seleccione los patrones que va a usar y se
          cargará la plantilla correspondiente.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {patronesDisponibles.map((p) => {
            const vigente = estadoPatronEn(p) === "vigente";
            return (
              <label
                key={p._id}
                className={`flex items-start gap-2 border rounded-lg p-2 text-sm ${
                  vigente ? "cursor-pointer hover:bg-gray-50" : "opacity-60"
                } ${sel.includes(String(p._id)) ? "border-green-400 bg-green-50" : "border-gray-200"}`}
              >
                <input
                  type="checkbox"
                  className="mt-0.5"
                  disabled={!vigente}
                  checked={sel.includes(String(p._id))}
                  onChange={() => togglePatron(p._id)}
                />
                <span className="min-w-0">
                  <span className="font-medium text-gray-800 block truncate">
                    {p.nombre}
                  </span>
                  <span className="text-xs text-gray-500 block">
                    {(p.variablesMedicion || [])
                      .map((v) => v.nombre)
                      .join(", ") || "—"}
                  </span>
                  {!vigente && (
                    <span className="text-xs text-red-600">
                      Certificado no vigente
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
        <div>
          <h4 className="font-semibold text-gray-700 text-sm mb-2">
            Plantillas compatibles
          </h4>
          {compatibles.length === 0 ? (
            <p className="text-xs text-gray-500 italic">
              {sel.length
                ? "Ninguna plantilla usa solo los patrones seleccionados."
                : "Seleccione al menos un patrón."}
            </p>
          ) : (
            <div className="space-y-2">
              {compatibles.map((t) => {
                const nombres = patronesDePlantilla(t)
                  .map(
                    (id) =>
                      patronesDisponibles.find((p) => String(p._id) === id)
                        ?.nombre,
                  )
                  .filter(Boolean);
                return (
                  <div
                    key={t._id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border rounded-lg p-2"
                  >
                    <div className="text-sm">
                      <div className="font-medium text-gray-800">
                        {t.nombre}{" "}
                        <span className="text-gray-400 font-normal">
                          v{t.version}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {nombres.length
                          ? `Patrones: ${nombres.join(", ")}`
                          : "Sin patrones vinculados (sin incertidumbre)"}
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => aplicarPlantilla(t)}
                    >
                      Usar plantilla
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 text-sm text-amber-800">
        <AlertTriangle size={16} className="inline mr-2" />
        No hay plantilla activa para la categoría{" "}
        <strong>{servicio?.equipo?.categoria?.name}</strong>. Ve a{" "}
        <em>Ajustes {">"} Categorías</em> para configurar una plantilla de
        calibración.
      </div>
    );
  }

  // Agrupar mediciones por variable (con su resultado calculado y su índice real)
  const variablesPlantilla = new Map(
    (template.variables || []).map((v) => [String(v._id), v]),
  );
  const reglaTolerancia = template.reglasAceptacion?.reglaTolerancia || "mayor";
  const variablesAgrupadas = [];
  mediciones.forEach((med, realIdx) => {
    const fila = { med, calc: calculo.mediciones[realIdx] || med, realIdx };
    const grupo = variablesAgrupadas.find(
      (g) => String(g.variableId) === String(med.variableId),
    );
    if (grupo) {
      grupo.puntos.push(fila);
    } else {
      const variable = variablesPlantilla.get(String(med.variableId)) || {};
      const vinculada = !!(variable.patron && variable.patronVariable);
      const patVinculado = vinculada
        ? patronesDisponibles.find(
            (p) => String(p._id) === idDe(variable.patron),
          )
        : null;
      const vpVinculada = patVinculado
        ? variableDePatron(patVinculado, variable.patronVariable)
        : null;
      variablesAgrupadas.push({
        vinculada,
        patronNombre: patVinculado
          ? `${patVinculado.nombre}${vpVinculada ? ` · ${vpVinculada.nombre}` : ""}`
          : variable.patronNombre || "patrón de la plantilla",
        variableId: med.variableId,
        nombre: med.nombreVariable,
        unidad: med.unidad,
        medida: esMedida(med),
        variable,
        ebcGenera: variable.metodo === "ebc_genera",
        capturaPatron:
          variable.metodo !== "ebc_genera" && variable.capturaPatron === true,
        puntos: [fila],
      });
    }
  });

  const tempPromedio = promedioAmb(ambiente.tempInicio, ambiente.tempFinal);
  const humPromedio = promedioAmb(ambiente.humInicio, ambiente.humFinal);
  const patronAmbSel = patronesDisponibles.find(
    (p) => String(p._id) === String(ambiente.patron),
  );
  const camposAmbiente = [
    { key: "tempInicio", label: "Temperatura min.", unidad: "°C" },
    { key: "tempFinal", label: "Temperatura max.", unidad: "°C" },
    { key: "humInicio", label: "Humedad min.", unidad: "%HR" },
    { key: "humFinal", label: "Humedad max.", unidad: "%HR" },
  ];

  const patronesElegibles = patronesDisponibles.filter((p) =>
    patronesSeleccionados.includes(p._id),
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-green-700">
          Ejecución de Calibración
        </h3>
        <div className="text-xs text-gray-500 text-right">
          <div>
            Plantilla: <strong>{template.nombre}</strong> v{template.version}
            {plantillasCandidatas.length > 1 &&
              !gestionExistente &&
              !modoSolo && (
                <button
                  type="button"
                  onClick={cambiarPlantilla}
                  className="ml-2 text-blue-600 hover:underline"
                >
                  Cambiar
                </button>
              )}
          </div>
          {!modoSolo && ultimoAutoguardado && (
            <div className="flex items-center justify-end gap-1 text-gray-400 mt-0.5">
              <Save size={11} />
              Guardado en este dispositivo{" "}
              {new Date(ultimoAutoguardado).toLocaleTimeString("es-CO")}
            </div>
          )}
        </div>
      </div>

      {!modoSolo && !enLinea && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3 rounded-lg">
          <WifiOff size={14} className="mt-0.5 flex-shrink-0" />
          <span>
            Sin conexión. Los datos que ingreses se siguen guardando en este
            dispositivo; guarda el borrador o finaliza cuando vuelva la
            conexión.
          </span>
        </div>
      )}

      {/* ── Memoria de calibración aplicada ── */}
      {!modoSolo && perfilAplicado && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-violet-50 border border-violet-200 text-violet-800 text-xs p-3 rounded-lg">
          <span className="flex items-start gap-2">
            <History size={14} className="mt-0.5 flex-shrink-0" />
            <span>
              Se aplicó la configuración recordada para{" "}
              <strong>
                {equipo?.marca?.name} {equipo?.modelo?.name}
              </strong>{" "}
              (variables medidas y patrones). Puedes ajustarla; al guardar se
              actualizará para los próximos equipos de este modelo.
            </span>
          </span>
          <button
            type="button"
            onClick={olvidarPerfil}
            className="flex items-center gap-1 self-start sm:self-auto text-violet-700 hover:text-violet-900 font-medium whitespace-nowrap"
          >
            <RotateCcw size={12} /> Olvidar y restablecer
          </button>
        </div>
      )}

      {/* ── Nota IA ── */}
      {template.generadoPorIA && (
        <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 text-blue-800 text-xs p-3 rounded-lg">
          <Info size={14} className="mt-0.5 flex-shrink-0" />
          <span>
            Esta plantilla fue generada con IA. Verifica que las tolerancias y
            puntos de calibración correspondan a la norma técnica aplicable.
          </span>
        </div>
      )}

      {/* ── Fecha de calibración ── */}
      <section className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-gray-500">Fecha de calibración</label>
          <input
            type="date"
            max={hoyLocal()}
            disabled={modoSolo}
            className="border rounded w-full px-2 py-1.5 text-sm mt-0.5 disabled:bg-gray-50"
            value={fechaCalibracion}
            onChange={(e) => setFechaCalibracion(e.target.value)}
          />
        </div>
      </section>

      {/* ── Parámetros fijos ── */}
      {template.parametrosFijos?.length > 0 && (
        <section>
          <h4 className="font-semibold text-gray-700 text-sm mb-2">
            Parámetros del reporte
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {template.parametrosFijos.map((p) => {
              // Independiente: título + descripción en el reporte → texto largo
              const independiente = p.formato === "independiente";
              const Campo = independiente ? "textarea" : "input";
              return (
                <div
                  key={p.clave}
                  className={independiente ? "sm:col-span-2" : ""}
                >
                  <label className="text-xs text-gray-500">
                    {p.etiqueta}{" "}
                    {p.obligatorio && <span className="text-red-500">*</span>}
                  </label>
                  <Campo
                    {...(independiente ? { rows: 3 } : { type: "text" })}
                    disabled={modoSolo}
                    className="border rounded w-full px-2 py-1.5 text-sm mt-0.5 disabled:bg-gray-50"
                    value={parametrosFijosForm[p.clave] || ""}
                    onChange={(e) =>
                      setParametrosFijosForm((prev) => ({
                        ...prev,
                        [p.clave]: e.target.value,
                      }))
                    }
                  />
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Patrones usados ── */}
      <section>
        <h4 className="font-semibold text-gray-700 text-sm mb-2">
          Patrones de medición utilizados{" "}
          <span className="text-red-500">*</span>
        </h4>
        {patronesDisponibles.length === 0 ? (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            No hay patrones registrados. Ve a{" "}
            <em>Ajustes {">"} Patrones de Calibración</em> para registrar los
            instrumentos.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {patronesDisponibles.map((p) => {
              const seleccionado = patronesSeleccionados.includes(p._id);
              const esVencido = estadoPatronEn(p, fechaVigencia) !== "vigente";
              const dePlantilla = patronesDePlantilla(template).includes(
                String(p._id),
              );
              return (
                <label
                  key={p._id}
                  className={`flex items-start gap-2 border rounded-lg p-2 cursor-pointer text-sm transition-colors ${
                    seleccionado
                      ? esVencido
                        ? "border-red-400 bg-red-50"
                        : "border-green-400 bg-green-50"
                      : "border-gray-200 hover:bg-gray-50"
                  } ${modoSolo ? "pointer-events-none" : ""}`}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={seleccionado}
                    disabled={modoSolo || dePlantilla}
                    title={
                      dePlantilla
                        ? "Patrón definido por la plantilla"
                        : undefined
                    }
                    onChange={() => togglePatron(p._id)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-800 truncate">
                      {p.nombre}
                    </div>
                    <div className="text-xs text-gray-500">
                      Serial: {p.serial || "—"} · Cert:{" "}
                      {p.numeroCertificado || "—"}
                    </div>
                    <div className="text-xs text-gray-500">
                      Vence:{" "}
                      {p.fechaVencimiento
                        ? new Date(p.fechaVencimiento).toLocaleDateString(
                            "es-CO",
                          )
                        : "—"}
                    </div>
                    {esVencido && (
                      <span className="text-xs text-red-600 font-medium">
                        ⚠{" "}
                        {estadoPatronEn(p, fechaVigencia) === "vencido"
                          ? "Certificado vencido"
                          : "Sin vigencia registrada"}
                      </span>
                    )}
                    {p.variablesMedicion?.length > 0 && (
                      <div className="text-xs text-gray-400">
                        {p.variablesMedicion
                          .map((v) =>
                            v.unidad ? `${v.nombre} (${v.unidad})` : v.nombre,
                          )
                          .join(", ")}
                      </div>
                    )}
                    {dePlantilla && (
                      <div className="text-xs text-indigo-600">
                        Definido por la plantilla
                      </div>
                    )}
                    {p.incertidumbreDeclarada && (
                      <div className="text-xs text-gray-400">
                        Incert.: {p.incertidumbreDeclarada}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </section>

      {/* ── Mediciones por variable ── */}
      <section>
        <h4 className="font-semibold text-gray-700 text-sm mb-3">
          Tabla de mediciones
        </h4>
        {template.instrucciones && (
          <p className="text-xs text-gray-500 italic mb-3 bg-gray-50 border rounded p-2">
            {template.instrucciones}
          </p>
        )}

        <div className="space-y-5">
          {variablesAgrupadas.map((grupo) => {
            const medsGrupo = grupo.puntos.map((f) => f.med);
            const patronVariable =
              patronPorVariable[grupo.variableId] ?? patronComun(medsGrupo);
            const patronesVariable = patronesElegibles.filter((p) =>
              patronCompatible(p, grupo.variable),
            );
            const maxReps = medsGrupo.reduce(
              (mx, m) => Math.max(mx, m.repeticiones?.length || 0),
              0,
            );
            const conU = grupo.puntos.some(
              (f) => num(f.calc.incertidumbreExpandida) !== null,
            );
            const conRef = grupo.puntos.some(
              (f) => num(f.calc.valorReferencia) !== null,
            );
            return (
              <div
                key={grupo.variableId}
                className={`border rounded-lg overflow-hidden ${
                  grupo.medida ? "" : "border-dashed"
                }`}
              >
                <div className="bg-gray-100 px-3 py-2 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                  <div className="flex items-center gap-3">
                    <label
                      className={`flex items-center gap-1.5 text-xs select-none ${
                        modoSolo ? "" : "cursor-pointer"
                      }`}
                      title={
                        grupo.medida
                          ? "Desactiva si esta variable no aplica a este equipo"
                          : "Activa para medir esta variable"
                      }
                    >
                      <input
                        type="checkbox"
                        role="switch"
                        className="sr-only peer"
                        checked={grupo.medida}
                        disabled={modoSolo}
                        onChange={(e) =>
                          setVariableMedida(grupo.variableId, e.target.checked)
                        }
                      />
                      <span
                        aria-hidden="true"
                        className="relative inline-block w-8 h-[18px] rounded-full bg-gray-300 transition-colors peer-checked:bg-green-600 peer-focus-visible:ring-2 peer-focus-visible:ring-blue-400 after:absolute after:top-0.5 after:left-0.5 after:w-3.5 after:h-3.5 after:rounded-full after:bg-white after:transition-transform peer-checked:after:translate-x-[14px]"
                      />
                      <span
                        className={
                          grupo.medida ? "text-green-700" : "text-gray-500"
                        }
                      >
                        {grupo.medida ? "Medir" : "No medir"}
                      </span>
                    </label>
                    <span
                      className={`font-medium text-sm ${
                        grupo.medida
                          ? "text-gray-700"
                          : "text-gray-400 line-through"
                      }`}
                    >
                      {grupo.nombre}
                      <span className="ml-2 text-gray-400 font-normal text-xs">
                        ({grupo.unidad})
                      </span>
                    </span>
                  </div>
                  {grupo.medida && (
                    <button
                      type="button"
                      onClick={() =>
                        setDetalleVariableId(String(grupo.variableId))
                      }
                      className="sm:ml-auto sm:mr-2 inline-flex items-center gap-1 text-xs text-green-700 bg-white border border-green-300 rounded px-2 py-1 hover:bg-green-50"
                    >
                      <Calculator size={13} />
                      Mostrar cálculos
                    </button>
                  )}
                  {grupo.medida && grupo.vinculada && (
                    <span className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1">
                      Patrón: <strong>{grupo.patronNombre}</strong>
                    </span>
                  )}
                  {grupo.medida && !grupo.vinculada && (
                    <label className="flex items-center gap-2 text-xs text-gray-600">
                      Patrón para todos los puntos:
                      <select
                        disabled={modoSolo}
                        className="border rounded px-1.5 py-1 text-xs w-44 bg-white disabled:bg-gray-50"
                        value={patronVariable}
                        onChange={(e) =>
                          aplicarPatronVariable(
                            grupo.variableId,
                            e.target.value,
                          )
                        }
                      >
                        <option value="">
                          {patronComun(medsGrupo) === "" &&
                          medsGrupo.some((m) =>
                            m.repeticiones.some((r) => r.patronId),
                          )
                            ? "Varios / seleccionar…"
                            : "Sin patrón"}
                        </option>
                        {patronesVariable.map((p) => (
                          <option key={p._id} value={p._id}>
                            {p.nombre}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                </div>

                {!grupo.medida ? (
                  <p className="px-3 py-2 text-xs text-gray-500 italic">
                    Esta variable no se medirá (no aplica a este equipo). No se
                    incluirá en el resultado global ni en las tablas del
                    reporte.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <p className="px-3 pt-2 text-[11px] text-gray-500">
                      {grupo.ebcGenera
                        ? "El equipo genera el valor nominal: registra en cada repetición la lectura del patrón."
                        : grupo.capturaPatron
                          ? "Registra en cada repetición la indicación del equipo (E) y la lectura del patrón (P)."
                          : "Registra en cada repetición la indicación del equipo bajo calibración."}
                    </p>
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-1.5 text-left text-gray-600 font-medium w-24">
                            Punto ref.
                          </th>
                          {Array.from({ length: maxReps }, (_, i) => (
                            <th
                              key={i}
                              className="px-2 py-1.5 text-gray-600 font-medium text-center"
                            >
                              Rep. {i + 1}
                            </th>
                          ))}
                          <th className="px-2 py-1.5 text-gray-600 font-medium text-center">
                            {grupo.ebcGenera ? "Prom. patrón" : "Promedio"}
                          </th>
                          {conRef && (
                            <th className="px-2 py-1.5 text-gray-600 font-medium text-center">
                              Ref. corregida
                            </th>
                          )}
                          <th className="px-2 py-1.5 text-gray-600 font-medium text-center">
                            Error
                          </th>
                          <th className="px-2 py-1.5 text-gray-600 font-medium text-center">
                            Error %
                          </th>
                          {conU && (
                            <th className="px-2 py-1.5 text-gray-600 font-medium text-center">
                              U (k)
                            </th>
                          )}
                          <th className="px-2 py-1.5 text-gray-600 font-medium text-center">
                            Decisión
                          </th>
                          <th className="px-2 py-1.5 text-gray-600 font-medium text-left">
                            Comentario
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {grupo.puntos.map(({ med, calc, realIdx }, medIdx) => {
                          const rango = rangoAceptacion(
                            med.puntoReferencia,
                            med._toleranciaAbs ?? null,
                            med._toleranciaPct ?? null,
                            reglaTolerancia,
                          );
                          const dec = decimalesPresentacion({
                            U: calc.incertidumbreExpandida,
                            resolucion: grupo.variable.resolucion,
                          });
                          const fmt = (x) => formatear(x, dec);
                          const avisos = calc.advertencias || [];
                          return (
                            <tr
                              key={medIdx}
                              className="border-t hover:bg-gray-50 align-top"
                            >
                              <td className="px-3 py-1.5 font-medium text-gray-700">
                                {med.puntosLabel || med.puntoReferencia}
                                {rango && (
                                  <div
                                    className="text-[10px] font-normal text-gray-400 whitespace-nowrap"
                                    title={`Tolerancia ${rango.etiqueta}${
                                      med._toleranciaPropia
                                        ? " (propia del punto)"
                                        : ""
                                    }`}
                                  >
                                    {rango.min} – {rango.max} {grupo.unidad}
                                    <span className="ml-1">
                                      ({rango.etiqueta})
                                    </span>
                                    {med._toleranciaPropia && (
                                      <span className="ml-1 text-blue-500">
                                        •
                                      </span>
                                    )}
                                  </div>
                                )}
                              </td>

                              {Array.from({ length: maxReps }, (_, repIdx) => {
                                const rep = med.repeticiones[repIdx];
                                if (!rep) return <td key={repIdx} />;
                                return (
                                  <td
                                    key={repIdx}
                                    className="px-2 py-1 text-center"
                                  >
                                    <input
                                      type="number"
                                      step="any"
                                      disabled={modoSolo}
                                      title={
                                        grupo.ebcGenera
                                          ? "Lectura del patrón"
                                          : "Indicación del equipo"
                                      }
                                      className="border rounded px-1.5 py-1 w-20 text-center text-xs disabled:bg-gray-50 focus:ring-1 focus:ring-blue-400"
                                      value={rep.valorMedido ?? ""}
                                      onChange={(e) =>
                                        updateRepeticion(
                                          realIdx,
                                          repIdx,
                                          "valorMedido",
                                          e.target.value,
                                        )
                                      }
                                      placeholder={
                                        grupo.capturaPatron ? "E" : "—"
                                      }
                                    />
                                    {grupo.capturaPatron && (
                                      <input
                                        type="number"
                                        step="any"
                                        disabled={modoSolo}
                                        title="Lectura del patrón"
                                        className="border border-blue-200 rounded px-1.5 py-1 w-20 mt-1 text-center text-xs disabled:bg-gray-50 focus:ring-1 focus:ring-blue-400"
                                        value={rep.valorPatron ?? ""}
                                        onChange={(e) =>
                                          updateRepeticion(
                                            realIdx,
                                            repIdx,
                                            "valorPatron",
                                            e.target.value,
                                          )
                                        }
                                        placeholder="P"
                                      />
                                    )}
                                  </td>
                                );
                              })}

                              <td className="px-2 py-1 text-center font-mono text-gray-700">
                                {fmt(calc.promedio)}
                              </td>
                              {conRef && (
                                <td className="px-2 py-1 text-center font-mono text-gray-600">
                                  {fmt(calc.valorReferencia)}
                                </td>
                              )}
                              <td className="px-2 py-1 text-center font-mono text-gray-600">
                                {fmt(calc.errorAbsoluto)}
                              </td>
                              <td className="px-2 py-1 text-center font-mono text-gray-600">
                                {num(calc.errorRelativo) !== null
                                  ? `${formatear(calc.errorRelativo, 2)}%`
                                  : "—"}
                              </td>
                              {conU && (
                                <td className="px-2 py-1 text-center font-mono text-gray-600 whitespace-nowrap">
                                  {num(calc.incertidumbreExpandida) !== null
                                    ? `±${fmt(calc.incertidumbreExpandida)}`
                                    : "—"}
                                  {num(calc.incertidumbre?.k) !== null &&
                                    num(calc.incertidumbreExpandida) !==
                                      null && (
                                      <span className="text-gray-400">
                                        {" "}
                                        (k={formatear(calc.incertidumbre.k, 2)})
                                      </span>
                                    )}
                                </td>
                              )}
                              <td className="px-2 py-1 text-center">
                                <DecisionBadge decision={calc.decision} />
                                {avisos.length > 0 && (
                                  <span
                                    className="block text-[10px] text-amber-600 mt-0.5 cursor-help"
                                    title={avisos.join("\n")}
                                  >
                                    ⚠ {avisos.length} aviso(s)
                                  </span>
                                )}
                              </td>

                              <td className="px-2 py-1">
                                <input
                                  type="text"
                                  disabled={modoSolo}
                                  className="border rounded px-1.5 py-1 text-xs w-36 disabled:bg-gray-50"
                                  value={med.comentario || ""}
                                  onChange={(e) =>
                                    updateMedicionField(
                                      realIdx,
                                      "comentario",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="Observación..."
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Validación (mismas reglas que el servidor) ── */}
      {!modoSolo &&
        (erroresValidacion.length > 0 || calculo.errores.length > 0) && (
          <div className="border border-red-200 bg-red-50 rounded-lg p-3 text-xs text-red-800">
            <div className="font-semibold mb-1 flex items-center gap-1">
              <AlertTriangle size={13} /> Revisa antes de finalizar
            </div>
            <ul className="list-disc ml-5 space-y-0.5">
              {[...new Set([...erroresValidacion, ...calculo.errores])].map(
                (e) => (
                  <li key={e}>{e}</li>
                ),
              )}
            </ul>
          </div>
        )}
      {calculo.advertencias?.length > 0 && (
        <details className="border border-amber-200 bg-amber-50 rounded-lg p-3 text-xs text-amber-800">
          <summary className="cursor-pointer font-semibold">
            {calculo.advertencias.length} advertencia(s) del cálculo
          </summary>
          <ul className="list-disc ml-5 mt-1 space-y-0.5">
            {calculo.advertencias.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </details>
      )}
      {template.incertidumbre?.calcular !== false &&
        calculo.tipoDocumento === "informe" &&
        calculo.mediciones.some((m) => num(m.errorAbsoluto) !== null) && (
          <p className="text-xs text-gray-600 bg-gray-50 border rounded p-2">
            <Info size={12} className="inline mr-1" />
            Sin incertidumbre en todos los puntos (faltan datos metrológicos del
            patrón): el documento se emitirá como{" "}
            <strong>informe de verificación</strong>, no como certificado de
            calibración.
          </p>
        )}

      {/* ── Resultado global ── */}
      {resultadoGlobal && (
        <div
          className={`flex items-center gap-2 p-3 rounded-lg border text-sm font-semibold ${
            resultadoGlobal === "Conforme"
              ? "bg-green-50 border-green-300 text-green-800"
              : resultadoGlobal === "No conforme"
                ? "bg-red-50 border-red-300 text-red-800"
                : resultadoGlobal === "Indeterminado"
                  ? "bg-amber-50 border-amber-300 text-amber-800"
                  : "bg-gray-50 border-gray-200 text-gray-600"
          }`}
        >
          {resultadoGlobal === "Conforme" ? (
            <CheckCircle size={16} />
          ) : (
            <AlertTriangle size={16} />
          )}
          Resultado global: {resultadoGlobal}
        </div>
      )}

      {/* ── Condiciones ambientales (ocultas en calibraciones finalizadas antes de existir) ── */}
      {(!modoSolo || gestionExistente?.condicionesAmbientales) && (
        <section className="border rounded-lg overflow-hidden">
          <div className="flex items-center justify-between gap-2 bg-gray-50 px-3 py-2 border-b">
            <h4 className="font-semibold text-gray-700 text-sm flex items-center gap-1.5">
              <Thermometer size={14} className="text-blue-600" />
              Condiciones ambientales
              {ambiente.incluir && <span className="text-red-500">*</span>}
            </h4>
            <div
              className="flex items-center gap-2 text-xs text-gray-600"
              title="Desactiva para excluir las condiciones ambientales de la planilla y del reporte"
            >
              <span>{ambiente.incluir ? "Incluidas" : "Excluidas"}</span>
              <button
                type="button"
                role="switch"
                aria-checked={ambiente.incluir}
                aria-label="Incluir condiciones ambientales"
                disabled={modoSolo}
                onClick={() =>
                  setAmbiente((prev) => ({ ...prev, incluir: !prev.incluir }))
                }
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-60 ${
                  ambiente.incluir ? "bg-blue-600" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    ambiente.incluir ? "translate-x-4" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>
          </div>
          {!ambiente.incluir ? (
            <p className="px-3 py-2 text-xs text-gray-500 italic">
              Las condiciones ambientales no se registrarán ni se incluirán en
              el reporte.
            </p>
          ) : (
            <div className="p-3 space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {camposAmbiente.map((c) => (
                  <div key={c.key}>
                    <label className="text-xs text-gray-500">
                      {c.label} ({c.unidad})
                    </label>
                    <input
                      type="number"
                      step="any"
                      disabled={modoSolo}
                      className="border rounded w-full px-2 py-1.5 text-sm mt-0.5 disabled:bg-gray-50"
                      value={ambiente[c.key]}
                      onChange={(e) =>
                        setAmbiente((prev) => ({
                          ...prev,
                          [c.key]: e.target.value,
                        }))
                      }
                    />
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 text-xs text-gray-600">
                <span>
                  Promedio temperatura:{" "}
                  <strong className="font-mono">
                    {tempPromedio !== null ? `${tempPromedio} °C` : "—"}
                  </strong>
                </span>
                <span>
                  Promedio humedad:{" "}
                  <strong className="font-mono">
                    {humPromedio !== null ? `${humPromedio} %HR` : "—"}
                  </strong>
                </span>
              </div>
              <div>
                <label className="text-xs text-gray-500">
                  Patrón utilizado (termohigrómetro){" "}
                  <span className="text-red-500">*</span>
                </label>
                <select
                  disabled={modoSolo}
                  className="border rounded w-full px-2 py-1.5 text-sm mt-0.5 disabled:bg-gray-50"
                  value={ambiente.patron}
                  onChange={(e) =>
                    setAmbiente((prev) => ({ ...prev, patron: e.target.value }))
                  }
                >
                  <option value="">— Seleccionar patrón —</option>
                  {patronesDisponibles.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.nombre}
                      {p.serial ? ` · S/N ${p.serial}` : ""}
                      {estadoPatronEn(p, fechaVigencia) !== "vigente" ? " (no vigente)" : ""}
                    </option>
                  ))}
                </select>
                {patronAmbSel && estadoPatronEn(patronAmbSel, fechaVigencia) !== "vigente" && (
                  <span className="text-xs text-red-600 font-medium">
                    ⚠ Certificado del patrón vencido
                  </span>
                )}
                {!modoSolo && (
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    Se recordará como predeterminado para tus próximas
                    calibraciones.
                  </p>
                )}
              </div>
            </div>
          )}
        </section>
      )}

      {/* ── Observaciones y conclusión ── */}
      <section>
        <label className="text-sm font-medium text-gray-700">
          Observaciones y conclusión
        </label>
        <textarea
          rows={3}
          disabled={modoSolo}
          className="border rounded-lg w-full px-3 py-2 mt-1 text-sm disabled:bg-gray-50"
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
          placeholder="Observaciones técnicas, condiciones ambientales, conclusión general..."
        />
      </section>

      {/* ── Acciones ── */}
      {!modoSolo && (
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={() => navigate(-1)} type="button">
            Volver
          </Button>
          <Button
            variant="outline"
            onClick={handleGuardarBorrador}
            disabled={guardando}
            className="border-blue-400 text-blue-700 hover:bg-blue-50"
          >
            {guardando ? "Guardando..." : "Guardar borrador"}
          </Button>
          <Button
            variant="outline"
            onClick={() => abrirVistaPrevia(false)}
            disabled={guardando || generandoVista || !template}
          >
            <Eye size={14} className="mr-1.5" />
            {generandoVista ? "Generando..." : "Vista previa"}
          </Button>
          <Button
            onClick={handleFinalizar}
            disabled={guardando || generandoVista}
            className="bg-green-600 hover:bg-green-700"
          >
            {guardando ? "Procesando..." : "Finalizar calibración"}
          </Button>
        </div>
      )}

      {/* ── Sticker de calibración ── */}
      {modoSolo && !soloRevision && (
        <div
          className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border p-3 ${
            sticker
              ? "border-green-200 bg-green-50"
              : "border-amber-200 bg-amber-50"
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`p-2 rounded-lg ${
                sticker
                  ? "bg-green-100 text-green-700"
                  : "bg-amber-100 text-amber-700"
              }`}
            >
              <Tag size={18} />
            </div>
            {sticker ? (
              <div className="text-sm">
                <div className="text-gray-500 text-xs">
                  Sticker / certificado N°
                </div>
                <div className="font-mono font-semibold text-gray-900">
                  {sticker.numero}
                </div>
              </div>
            ) : (
              <div className="text-sm text-amber-800">
                Esta calibración aún no tiene sticker asignado.
              </div>
            )}
          </div>
          {sticker ? (
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                className="bg-white"
                onClick={() =>
                  window.open(urlCertificadoPublico(sticker.token), "_blank")
                }
              >
                <ExternalLink size={14} className="mr-1.5" />
                Ver página pública
              </Button>
              <Button
                type="button"
                className="bg-green-600 hover:bg-green-700"
                onClick={() => imprimirSticker(sticker._id)}
              >
                <Printer size={14} className="mr-1.5" />
                Imprimir sticker
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => setModalSticker("asignar")}
            >
              <Tag size={14} className="mr-1.5" />
              Asignar sticker
            </Button>
          )}
        </div>
      )}

      <DetalleCalculosModal
        grupo={variablesAgrupadas.find(
          (g) => String(g.variableId) === detalleVariableId,
        )}
        // Finalizada: patrones de la instantánea sellada
        patrones={
          (modoSolo && gestionExistente?.snapshot?.patrones) ||
          patronesDisponibles
        }
        onClose={() => setDetalleVariableId(null)}
      />

      <VistaPreviaReporteModal
        url={vistaPrevia?.url}
        paraFinalizar={vistaPrevia?.paraFinalizar}
        onClose={cerrarVistaPrevia}
        onConfirmar={confirmarDesdeVistaPrevia}
      />

      <AsignarStickerModal
        open={modalSticker !== null}
        onClose={() => setModalSticker(null)}
        procesando={guardando}
        titulo={
          modalSticker === "finalizar"
            ? "Finalizar calibración"
            : "Asignar sticker de calibración"
        }
        textoBoton={
          modalSticker === "finalizar"
            ? "Finalizar y asignar"
            : "Asignar sticker"
        }
        aviso={
          modalSticker === "finalizar"
            ? "Al finalizar no podrás editar los datos de la calibración."
            : undefined
        }
        onConfirm={
          modalSticker === "finalizar"
            ? finalizarConSticker
            : handleAsignarDespues
        }
      />

      {modoSolo && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-gray-500 italic border-t pt-2">
          <span>
            Esta calibración está finalizada y no puede editarse. Descarga el
            reporte para trazabilidad.
          </span>
          <Button
            type="button"
            variant="outline"
            className="text-violet-700 border-violet-300 hover:bg-violet-50 w-full sm:w-auto"
            onClick={() => descargarReporteIndividual(servicio?._id)}
          >
            {calculo.tipoDocumento === "certificado"
              ? "Descargar certificado"
              : "Descargar informe"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default FormCalibracion;
